import { PencilSimpleIcon, TrashIcon, KeyIcon } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DeactivateUserConfirmDialog } from '@/components/users/DeactivateUserConfirmDialog';
import { ResetPasswordDialog } from '@/components/users/ResetPasswordDialog';
import { UnlockButton } from '@/components/users/UnlockButton';
import { UserActivityLog } from '@/components/users/UserActivityLog';
import { ApiError } from '@/lib/api/error';
import { useAuth } from '@/lib/auth/AuthContext';
import { getUserRequest } from '@/services/users';
import type { UserDetail, UserStatus } from '@/types/users';

function formatDateTime(iso: string | null): string {
  if (iso === null) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function deriveStatus(u: UserDetail): UserStatus {
  if (!u.isActive) return 'Inactive';
  if (u.lockedUntil !== null && new Date(u.lockedUntil).getTime() > Date.now()) return 'Locked';
  return 'Active';
}

function statusVariant(s: UserStatus): 'default' | 'secondary' | 'destructive' {
  switch (s) {
    case 'Active':
      return 'default';
    case 'Inactive':
      return 'secondary';
    case 'Locked':
      return 'destructive';
  }
}

export function UserDetailPage(): JSX.Element {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);
  const { permissions } = useAuth();
  const navigate = useNavigate();

  const [resetOpen, setResetOpen] = useState<boolean>(false);
  const [deactivateOpen, setDeactivateOpen] = useState<boolean>(false);

  const detailQuery = useQuery<UserDetail, ApiError>({
    queryKey: ['user', id],
    queryFn: () => getUserRequest(id),
    enabled: Number.isFinite(id) && id > 0,
  });

  if (!permissions.includes('users.read')) {
    return <Navigate to="/dashboard" replace />;
  }
  if (!Number.isFinite(id) || id <= 0) {
    return <Navigate to="/users" replace />;
  }

  const canEdit = permissions.includes('users.update');
  const canDeactivate = permissions.includes('users.delete');

  const user = detailQuery.data;
  const status = user !== undefined ? deriveStatus(user) : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{user?.fullName ?? 'User'}</h1>
          <p className="text-muted-foreground">User profile, role, and activity history.</p>
        </div>
        <div className="flex items-center gap-2">
          {user !== undefined && canEdit && (
            <Button asChild data-testid="user-detail-edit-button">
              <Link to={`/users/${String(id)}/edit`}>
                <PencilSimpleIcon size={16} weight="bold" /> Edit
              </Link>
            </Button>
          )}
          {user !== undefined && canEdit && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setResetOpen(true);
              }}
              data-testid="user-detail-reset-password-button"
            >
              <KeyIcon size={16} weight="bold" /> Reset Password
            </Button>
          )}
          {user !== undefined && <UnlockButton user={user} permissions={permissions} />}
          {user !== undefined && canDeactivate && user.isActive && (
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                setDeactivateOpen(true);
              }}
              data-testid="user-detail-deactivate-button"
            >
              <TrashIcon size={16} weight="bold" /> Deactivate
            </Button>
          )}
        </div>
      </div>

      {detailQuery.isLoading && (
        <p className="text-sm text-muted-foreground" data-testid="user-detail-loading">
          Loading user…
        </p>
      )}
      {detailQuery.isError && (
        <p className="text-sm text-destructive" data-testid="user-detail-error">
          Failed to load user.
        </p>
      )}

      {user && status !== null && (
        <Tabs defaultValue="profile" className="space-y-4">
          <TabsList data-testid="user-detail-tabs">
            <TabsTrigger value="profile" data-testid="user-detail-tab-profile">
              Profile
            </TabsTrigger>
            <TabsTrigger value="activity" data-testid="user-detail-tab-activity">
              Activity Log
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle>Profile</CardTitle>
                <CardDescription>Read-only view. Use Edit to make changes.</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
                <div>
                  <div className="text-muted-foreground text-xs">Status</div>
                  <Badge variant={statusVariant(status)} data-testid="user-detail-status">
                    {status}
                  </Badge>
                </div>
                <div>
                  <div className="text-muted-foreground text-xs">Email</div>
                  <div data-testid="user-detail-email">{user.email}</div>
                </div>
                <div>
                  <div className="text-muted-foreground text-xs">Employee ID</div>
                  <div data-testid="user-detail-employeeid">{user.employeeId ?? '—'}</div>
                </div>
                <div>
                  <div className="text-muted-foreground text-xs">Phone</div>
                  <div data-testid="user-detail-phone">{user.phoneNumber ?? '—'}</div>
                </div>
                <div>
                  <div className="text-muted-foreground text-xs">Role</div>
                  <div data-testid="user-detail-role">{user.roleName ?? '—'}</div>
                </div>
                <div>
                  <div className="text-muted-foreground text-xs">Sites</div>
                  <div className="flex flex-wrap gap-1" data-testid="user-detail-site">
                    {user.sites.length === 0 && '—'}
                    {user.sites.map((s) => (
                      <span
                        key={s.siteId}
                        className="inline-flex rounded-full border bg-muted/40 px-2 py-0.5 text-xs"
                        data-testid={`user-detail-site-chip-${String(s.siteId)}`}
                      >
                        {s.siteName}
                        {!s.isActive && ' (inactive)'}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground text-xs">Work orders</div>
                  <div className="flex flex-wrap gap-1" data-testid="user-detail-workorders">
                    {user.workOrders.length === 0 && '—'}
                    {user.workOrders.map((w) => (
                      <span
                        key={w.workOrderId}
                        className="inline-flex rounded-full border bg-muted/40 px-2 py-0.5 text-xs"
                        data-testid={`user-detail-workorder-chip-${String(w.workOrderId)}`}
                      >
                        {w.woNumber}
                        {!w.isActive && ' (inactive)'}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground text-xs">Failed Logins</div>
                  <div data-testid="user-detail-failed-logins">{user.failedLoginAttempts}</div>
                </div>
                <div>
                  <div className="text-muted-foreground text-xs">Password change required</div>
                  <div>{user.passwordChangeRequired ? 'Yes' : 'No'}</div>
                </div>
                <div>
                  <div className="text-muted-foreground text-xs">Locked until</div>
                  <div className="font-mono text-xs">{formatDateTime(user.lockedUntil)}</div>
                </div>
                <div>
                  <div className="text-muted-foreground text-xs">Last login</div>
                  <div className="font-mono text-xs">{formatDateTime(user.lastLoginAt)}</div>
                </div>
                <div>
                  <div className="text-muted-foreground text-xs">Last password change</div>
                  <div className="font-mono text-xs">
                    {formatDateTime(user.lastPasswordChangeDate)}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground text-xs">Created</div>
                  <div className="font-mono text-xs">{formatDateTime(user.createdAt)}</div>
                </div>
                <div>
                  <div className="text-muted-foreground text-xs">Updated</div>
                  <div className="font-mono text-xs">{formatDateTime(user.updatedAt)}</div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="activity">
            <Card>
              <CardHeader>
                <CardTitle>Activity Log</CardTitle>
                <CardDescription>
                  Recent login history and admin actions for this user.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <UserActivityLog userId={id} />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {user !== undefined && (
        <>
          <ResetPasswordDialog
            open={resetOpen}
            onOpenChange={setResetOpen}
            userId={user.id}
            userFullName={user.fullName}
          />
          <DeactivateUserConfirmDialog
            open={deactivateOpen}
            onOpenChange={setDeactivateOpen}
            userId={user.id}
            userFullName={user.fullName}
            onSuccess={() => {
              void navigate('/users');
            }}
          />
        </>
      )}
    </div>
  );
}
